import { STORAGE_KEY, todayKey, type RuleId, type GameMode } from './constants';

export interface PlacedToolSave {
  toolId: string;
  x: number;
  y: number;
  angle: number;
  flipX?: boolean;
}

export interface Discovery {
  id: string;
  title: string;
  detail: string;
  ts: number;
  mode: string;
  rule?: string;
}

export interface ChallengeProgress {
  id: string;
  bestScore: number;
  completed: boolean;
  attempts: number;
  lastAt?: number;
}

export interface ReplayFrame {
  t: number;
  botX: number;
  botY: number;
  botAngle: number;
}

export interface SavedReplay {
  id: string;
  ts: number;
  challengeId?: string;
  mode: string;
  distance: number;
  frames: ReplayFrame[];
  tools: PlacedToolSave[];
  rule: RuleId;
}

export interface GameSave {
  version: 1;
  updatedAt: number;
  unlockedTools: string[];
  discoveries: Discovery[];
  challenges: Record<string, ChallengeProgress>;
  freeLab: {
    tools: PlacedToolSave[];
    rule: RuleId;
  };
  quick: {
    completedOnce: boolean;
    bestDistance: number;
    unlockedMagnet: boolean;
    unlockedLowGravity: boolean;
    runCount: number;
  };
  daily: {
    date: string;
    completed: boolean;
    bestScore: number;
  };
  settings: {
    muted: boolean;
  };
  lastMode: GameMode;
  replays: SavedReplay[];
  shareCodes: string[];
}

const defaultSave = (): GameSave => ({
  version: 1,
  updatedAt: Date.now(),
  unlockedTools: ['spring', 'fan', 'ramp'],
  discoveries: [],
  challenges: {},
  freeLab: { tools: [], rule: 'normal' },
  quick: {
    completedOnce: false,
    bestDistance: 0,
    unlockedMagnet: false,
    unlockedLowGravity: false,
    runCount: 0,
  },
  daily: {
    date: todayKey(),
    completed: false,
    bestScore: 0,
  },
  settings: { muted: false },
  lastMode: 'quick',
  replays: [],
  shareCodes: [],
});

export function loadSave(): GameSave {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSave();
    const parsed = JSON.parse(raw) as GameSave;
    if (!parsed || parsed.version !== 1) return defaultSave();
    const base = defaultSave();
    const merged: GameSave = {
      ...base,
      ...parsed,
      quick: { ...base.quick, ...parsed.quick },
      freeLab: { ...base.freeLab, ...parsed.freeLab },
      daily: { ...base.daily, ...parsed.daily },
      settings: { ...base.settings, ...parsed.settings },
      unlockedTools: parsed.unlockedTools?.length ? parsed.unlockedTools : base.unlockedTools,
      discoveries: parsed.discoveries || [],
      challenges: parsed.challenges || {},
      replays: parsed.replays || [],
      shareCodes: parsed.shareCodes || [],
    };
    if (merged.daily.date !== todayKey()) {
      merged.daily = { date: todayKey(), completed: false, bestScore: 0 };
    }
    return merged;
  } catch {
    return defaultSave();
  }
}

export function writeSave(save: GameSave): void {
  try {
    save.updatedAt = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(save));
  } catch {
    /* ignore */
  }
}

export function addDiscovery(save: GameSave, d: Omit<Discovery, 'ts' | 'id'> & { id?: string }): GameSave {
  const id = d.id || `d_${Date.now().toString(36)}`;
  if (save.discoveries.some((x) => x.id === id || x.title === d.title)) return save;
  save.discoveries.unshift({
    id,
    title: d.title,
    detail: d.detail,
    mode: d.mode,
    rule: d.rule,
    ts: Date.now(),
  });
  save.discoveries = save.discoveries.slice(0, 50);
  writeSave(save);
  return save;
}

/** Compact scene code for sharing */
export function encodeSceneCode(payload: {
  tools: PlacedToolSave[];
  rule: RuleId;
  mode?: string;
  challengeId?: string;
}): string {
  const json = JSON.stringify({
    v: 1,
    t: payload.tools.map((x) => [x.toolId, Math.round(x.x), Math.round(x.y), Math.round(x.angle), x.flipX ? 1 : 0]),
    r: payload.rule,
    m: payload.mode,
    c: payload.challengeId,
  });
  try {
    return btoa(unescape(encodeURIComponent(json))).replace(/=+$/, '');
  } catch {
    return '';
  }
}

export function decodeSceneCode(code: string): {
  tools: PlacedToolSave[];
  rule: RuleId;
  mode?: string;
  challengeId?: string;
} | null {
  try {
    const pad = code.length % 4 === 0 ? code : code + '='.repeat(4 - (code.length % 4));
    const json = decodeURIComponent(escape(atob(pad)));
    const data = JSON.parse(json) as {
      v: number;
      t: [string, number, number, number, number][];
      r: RuleId;
      m?: string;
      c?: string;
    };
    if (data.v !== 1 || !Array.isArray(data.t)) return null;
    return {
      tools: data.t.map(([toolId, x, y, angle, flip]) => ({
        toolId,
        x,
        y,
        angle,
        flipX: !!flip,
      })),
      rule: data.r || 'normal',
      mode: data.m,
      challengeId: data.c,
    };
  } catch {
    return null;
  }
}
